import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  ClockCounterClockwise,
  FileText,
  Package,
  NotePencil
} from '@phosphor-icons/react';
import { useTaskTypes } from '../../../hooks/useTaskTypes.js';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';
import TaskTabs from '../components/TaskTabs.jsx';
import GeneralTab from '../components/viewTaskTabs/GeneralTab.jsx';
import ValidacionTab from '../components/viewTaskTabs/ValidacionTab.jsx';
import MaterialsTab from '../components/viewTaskTabs/MaterialsTab.jsx';
import DocumentsTab from '../components/viewTaskTabs/DocumentsTab.jsx';
import FormsTab from '../components/viewTaskTabs/FormsTab.jsx';
import HistoryTab from '../components/viewTaskTabs/HistoryTab.jsx';
import { AuthContext } from '../../../context/authContext.jsx';
import { taskService } from '../../../services/tasks/taskService.js';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';

const PRIORITY_META = {
  baja: {
    label: 'Prioridad Baja',
    className: 'bg-info/12 text-info border border-info'
  },
  media: {
    label: 'Prioridad Media',
    className: 'bg-warning/12 text-warning border border-warning'
  },
  alta: {
    label: 'Prioridad Alta',
    className: 'bg-error/12 text-error border border-error'
  }
};

const TASK_TABS = [
  { key: 'general', label: 'Información General', icon: FileText },
  { key: 'validacion', label: 'Validación', icon: FileText, allowedRoles: ['admin'] },
  { key: 'materials', label: 'Materiales', icon: Package, enabled: false },
  { key: 'documents', label: 'Documentos', icon: FileText },
  { key: 'forms', label: 'Formularios', icon: NotePencil },
  { key: 'history', label: 'Historial', icon: ClockCounterClockwise }
];

const INITIAL_TAB_STATE = {
  general: {
    loading: false,
    loaded: false,
    error: null,
    data: null
  }
};

const fetchTaskDetail = async (taskId) => {
  return taskService.getTask(taskId);
};

function ViewTask({ taskId, onBack, onViewTask }) {
  const [activeTab, setActiveTab] = useState('general');
  // Recorrido guiado (taskDetailTour): cada paso de pestaña la activa.
  useGuidedTourStep((step) => {
    if (step?.detailTab) setActiveTab(step.detailTab);
  });
  const [generalReloadKey, setGeneralReloadKey] = useState(0);
  const [tabState, setTabState] = useState(INITIAL_TAB_STATE);
  const { taskTypes } = useTaskTypes();
  const { user } = useContext(AuthContext);
  const userRole = user?.role;
  const generalTask = tabState.general.data;
  const taskStatusNormalized = String(
    generalTask?.status ?? generalTask?.taskStatus ?? generalTask?.state ?? ''
  )
    .trim()
    .toLowerCase();

  const canShowValidationTab = ['finalizada', 'cancelada', 'finalizado', 'cancelado'].includes(
    taskStatusNormalized
  );

  const availableTabs = useMemo(() => {
    return TASK_TABS.filter((tab) => {
      if (tab.enabled === false) {
        return false;
      }

      if (tab.key === 'validacion' && !canShowValidationTab) {
        return false;
      }

      if (!tab.allowedRoles || tab.allowedRoles.length === 0) {
        return true;
      }

      if (!userRole) {
        return false;
      }

      return tab.allowedRoles.includes(userRole);
    });
  }, [canShowValidationTab, userRole]);

  useEffect(() => {
    setActiveTab('general');
    setTabState(INITIAL_TAB_STATE);
  }, [taskId]);

  useEffect(() => {
    if (!availableTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab('general');
    }
  }, [activeTab, availableTabs]);

  useEffect(() => {
    if (activeTab !== 'general' || !taskId || tabState.general.loaded) {
      return;
    }

    let cancelled = false;

    const loadGeneralInfo = async () => {
      setTabState((current) => ({
        ...current,
        general: {
          ...current.general,
          loading: true,
          error: null
        }
      }));

      try {
        const task = await fetchTaskDetail(taskId);

        if (cancelled) {
          return;
        }

        setTabState((current) => ({
          ...current,
          general: {
            loading: false,
            loaded: true,
            error: null,
            data: task
          }
        }));
      } catch (error) {
        if (cancelled) {
          return;
        }

        setTabState((current) => ({
          ...current,
          general: {
            ...current.general,
            loading: false,
            loaded: false,
            error: 'No se pudo cargar la información general de la tarea.'
          }
        }));
      }
    };

    loadGeneralInfo();

    return () => {
      cancelled = true;
    };
  }, [activeTab, taskId, tabState.general.loaded, generalReloadKey]);

  const taskTypeLabel = useMemo(() => {
    if (!generalTask) {
      return 'Tarea';
    }

    return taskTypes.find((item) => item.name === generalTask.taskTypeId)?.displayName
      || generalTask.taskTypeName
      || generalTask.taskType
      || generalTask.taskTypeId
      || 'Tarea';
  }, [generalTask, taskTypes]);

  const priorityMeta = PRIORITY_META[generalTask?.priority] || {
    label: generalTask?.priority || 'Sin prioridad',
    className: 'bg-surface-2 text-content-muted border border-line'
  };

  const teamName = useMemo(() => {
    if (!generalTask) {
      return 'Sin equipo asignado';
    }

    return generalTask.team?.name
      || generalTask.teamName
      || generalTask.teamId
      || 'Sin equipo asignado';
  }, [generalTask]);

    const refreshGeneralTask = async () => {
      if (!taskId) {
        return;
      }

      const task = await fetchTaskDetail(taskId);

      setTabState((current) => ({
        ...current,
        general: {
          ...current.general,
          data: task,
          loaded: true,
          error: null
        }
      }));
    };

    const tabContentByKey = {
      general: (
        <GeneralTab
          loading={tabState.general.loading}
          error={tabState.general.error}
          task={generalTask}
          taskTypeLabel={taskTypeLabel}
          priorityLabel={priorityMeta.label}
          teamName={teamName}
          onViewTask={onViewTask}
          onRetry={() => {
            setTabState((current) => ({
              ...current,
              general: { ...INITIAL_TAB_STATE.general }
            }));
            setGeneralReloadKey((current) => current + 1);
          }}
          onActionComplete={refreshGeneralTask}
        />
      ),
      validacion: <ValidacionTab taskId={taskId} task={generalTask} />,
      materials: <MaterialsTab />,
      documents: <DocumentsTab taskId={taskId} />,
      forms: <FormsTab taskId={taskId} />,
      history: <HistoryTab taskId={taskId} />
    };

  const renderActiveTab = () => {
      return tabContentByKey[activeTab] || null;
  };

  return (
    <div className="space-y-6">
      {generalTask && (
        <PageHeader
          title={generalTask.taskTypeName}
          subtitle={generalTask.title}
          icon={<FileText size={28} />}
          onBack={onBack}
        />
      )}
      

      {/* Task Tabs */}
      <div data-tour="task-tabs" className="rounded-3xl shadow-lg shadow-black/10">
        <TaskTabs tabs={availableTabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <div data-tour="task-detail-content">{renderActiveTab()}</div>
    </div>
  );
}

export default ViewTask;
